import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { PixChargeStatus, PixReportResponse } from '@aeris/contracts'
import { PIX_STATUS_TEXT, pixDayLabel } from '@aeris/status-meta'
import {
  Badge,
  Field,
  ListToolbar,
  Page,
  PageHeader,
  Pagination,
  ResultCount,
  Section,
  Select,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { ExportMenu } from '../components/export-menu.tsx'
import { PeriodPicker } from '../components/period-picker.tsx'
import { fetchBranches } from '../lib/api/organization.ts'
import {
  dateValue,
  numberValue,
  textValue,
  type ExportColumn,
} from '../lib/export/table-export.ts'
import { money } from '../lib/inbox-commerce.ts'
import {
  customRange,
  exclusiveEnd,
  periodRange,
  type PeriodChoice,
} from '../lib/period-range.ts'
import { fetchPixReport } from '../lib/pix-charge.ts'

/**
 * Pix recebidos — F205.
 *
 * O dono: *"ele vai ter que saber quanto em Pix entrou, dia, caixa e admin,
 * todas as info que precisa"*. O sistema não dá baixa em nada; esta tela é a
 * conferência: quanto foi pago, por quem foi gerado, de que filial, e se o
 * caixa já viu o aviso.
 */

type Item = PixReportResponse['items'][number]

const PAGE_SIZE = 25
const EXPORT_PAGE_SIZE = 200

const STATUS_LABEL: Record<PixChargeStatus, string> = Object.fromEntries(
  Object.entries(PIX_STATUS_TEXT).map(([key, text]) => [key, text.label])
) as Record<PixChargeStatus, string>

const stamp = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})
const at = (iso: string | null): string =>
  iso === null ? '—' : stamp.format(new Date(iso))

const PIX_EXPORT: readonly ExportColumn<Item>[] = [
  {
    id: 'gerado',
    label: 'Gerado em',
    value: (row) => dateValue(row.createdAt),
  },
  { id: 'pago', label: 'Pago em', value: (row) => dateValue(row.paidAt) },
  { id: 'valor', label: 'Valor', value: (row) => numberValue(row.amount) },
  { id: 'pagador', label: 'Pagador', value: (row) => row.payerName },
  {
    id: 'vendedor',
    label: 'Vendedor',
    value: (row) => textValue(row.createdByName),
  },
  { id: 'filial', label: 'Filial', value: (row) => textValue(row.branchName) },
  { id: 'status', label: 'Status', value: (row) => STATUS_LABEL[row.status] },
  {
    id: 'visto_por',
    label: 'Visto por',
    value: (row) => textValue(row.acknowledgedByName),
  },
  {
    id: 'visto_em',
    label: 'Visto em',
    value: (row) => dateValue(row.acknowledgedAt),
  },
]

export const PixPage = (): ReactNode => {
  const [period, setPeriod] = useState<PeriodChoice>('today')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [branchId, setBranchId] = useState('')
  const [createdBy, setCreatedBy] = useState('')
  const [status, setStatus] = useState<PixChargeStatus | ''>('')
  const [page, setPage] = useState(1)

  const range =
    period === 'custom'
      ? customRange(customFrom, customTo)
      : periodRange(period)

  const filter = {
    ...(range === null ? {} : { from: range.from, to: exclusiveEnd(range.to) }),
    ...(branchId === '' ? {} : { branchId }),
    ...(createdBy === '' ? {} : { createdBy }),
    ...(status === '' ? {} : { status }),
  }

  const report = useQuery({
    queryKey: ['pix-report', filter, page],
    queryFn: async ({ signal }) =>
      await fetchPixReport({ ...filter, page, pageSize: PAGE_SIZE }, signal),
    placeholderData: keepPreviousData,
  })

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: async ({ signal }) => await fetchBranches(undefined, signal),
  })

  const data = report.data
  const loading = report.isPending
  const error = report.isError
  const sellerName =
    data?.bySeller.find((seller) => seller.userId === createdBy)?.name ?? null

  return (
    <Page>
      <PageHeader
        title="Pix recebidos"
        description="O que entrou em Pix pelo atendimento. O sistema só avisa o caixa; a baixa é do caixa."
      />

      <ListToolbar>
        <PeriodPicker
          choice={period}
          onChoiceChange={(choice) => {
            setPeriod(choice)
            setPage(1)
          }}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
        />
        {(branches.data?.branches.length ?? 0) > 1 ? (
          <Field label="Filial">
            {(field) => (
              <Select
                {...field}
                value={branchId}
                onChange={(event) => {
                  setBranchId(event.target.value)
                  setPage(1)
                }}
              >
                <option value="">Todas as filiais</option>
                {(branches.data?.branches ?? []).map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}
        <Field label="Status">
          {(field) => (
            <Select
              {...field}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as PixChargeStatus | '')
                setPage(1)
              }}
            >
              <option value="">Todos</option>
              {(Object.keys(STATUS_LABEL) as PixChargeStatus[]).map((key) => (
                <option key={key} value={key}>
                  {STATUS_LABEL[key]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {createdBy === '' ? null : (
          <Badge tone="info">
            Vendedor: {sellerName ?? '—'}{' '}
            <button
              type="button"
              className="ml-1 underline"
              onClick={() => {
                setCreatedBy('')
                setPage(1)
              }}
            >
              limpar
            </button>
          </Badge>
        )}
      </ListToolbar>

      <section
        aria-label="Resumo do Pix"
        className="grid grid-cols-2 gap-2 lg:grid-cols-4"
      >
        <StatTile
          label="Pix recebido"
          loading={loading}
          error={error}
          value={
            <span className="font-(--font-mono) tabular-nums text-(--success)">
              {money(data?.paidAmount)}
            </span>
          }
          hint={
            data === undefined
              ? undefined
              : `${String(data.paidCount)} ${data.paidCount === 1 ? 'Pix' : 'Pix'} · ticket médio ${money(data.averageTicket)}`
          }
        />
        <StatTile
          label="Aguardando pagamento"
          loading={loading}
          error={error}
          value={
            <span className="font-(--font-mono) tabular-nums">
              {money(data?.pendingAmount)}
            </span>
          }
          hint={
            data === undefined ? undefined : `${String(data.pendingCount)} Pix`
          }
        />
        <StatTile
          label="Expirados"
          loading={loading}
          error={error}
          value={
            <span className="font-(--font-mono) tabular-nums text-(--text-muted)">
              {money(data?.expiredAmount)}
            </span>
          }
          hint={
            data === undefined
              ? undefined
              : `${String(data.expiredCount)} Pix sem pagamento em 24 h`
          }
        />
        <StatTile
          label="Pagos que o caixa não viu"
          loading={loading}
          error={error}
          value={
            <span
              className={
                (data?.unseenCount ?? 0) > 0 ? 'text-(--danger)' : undefined
              }
            >
              {String(data?.unseenCount ?? 0)}
            </span>
          }
        />
      </section>

      <div className="grid gap-3 lg:grid-cols-3">
        <Section title="Por dia">
          <SmallTable
            rows={(data?.byDay ?? []).map((row) => ({
              key: row.day,
              label: pixDayLabel(row.day),
              amount: row.amount,
              count: row.count,
            }))}
          />
        </Section>
        <Section title="Por vendedor" description="Clique para filtrar.">
          <SmallTable
            rows={(data?.bySeller ?? []).map((row) => ({
              key: row.userId ?? 'none',
              label: row.name,
              amount: row.amount,
              count: row.count,
              onClick:
                row.userId === null
                  ? undefined
                  : () => {
                      setCreatedBy(row.userId ?? '')
                      setPage(1)
                    },
            }))}
          />
        </Section>
        <Section title="Por filial">
          <SmallTable
            rows={(data?.byBranch ?? []).map((row) => ({
              key: row.branchId ?? 'none',
              label: row.name,
              amount: row.amount,
              count: row.count,
            }))}
          />
        </Section>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <ResultCount
          page={data?.page ?? page}
          pageSize={PAGE_SIZE}
          total={data?.total ?? 0}
          loading={loading}
          none="Nenhum Pix neste período"
        />
        <ExportMenu
          fileName="pix-recebidos"
          sheetName="Pix"
          columns={PIX_EXPORT}
          fetchPage={async (wanted) => {
            const response = await fetchPixReport({
              ...filter,
              page: wanted,
              pageSize: EXPORT_PAGE_SIZE,
            })
            return { items: response.items, total: response.total }
          }}
          pageSize={EXPORT_PAGE_SIZE}
          total={data?.total ?? 0}
        />
      </div>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Gerado</TableHeaderCell>
              <TableHeaderCell>Pago</TableHeaderCell>
              <TableHeaderCell className="text-right">Valor</TableHeaderCell>
              <TableHeaderCell>Pagador</TableHeaderCell>
              <TableHeaderCell>Vendedor</TableHeaderCell>
              <TableHeaderCell>Filial</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Caixa viu</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.items ?? []).map((item) => (
              <TableRow key={item.id}>
                <TableCell>{at(item.createdAt)}</TableCell>
                <TableCell>{at(item.paidAt)}</TableCell>
                <TableCell className="text-right font-(--font-mono) tabular-nums">
                  {money(item.amount)}
                </TableCell>
                <TableCell>{item.payerName}</TableCell>
                <TableCell>{item.createdByName ?? '—'}</TableCell>
                <TableCell>{item.branchName ?? '—'}</TableCell>
                <TableCell>
                  <Badge tone={PIX_STATUS_TEXT[item.status].tone}>
                    {STATUS_LABEL[item.status]}
                  </Badge>
                </TableCell>
                <TableCell>
                  {item.status !== 'paid' ? (
                    '—'
                  ) : item.acknowledgedAt === null ? (
                    <Badge tone="danger">Não viu</Badge>
                  ) : (
                    `${item.acknowledgedByName ?? ''} ${at(item.acknowledgedAt)}`
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination
        page={data?.page ?? page}
        pageSize={PAGE_SIZE}
        total={data?.total ?? 0}
        onPageChange={setPage}
        label="Páginas dos Pix"
      />
    </Page>
  )
}

const SmallTable = ({
  rows,
}: {
  rows: readonly {
    key: string
    label: string
    amount: string
    count: number
    onClick?: (() => void) | undefined
  }[]
}): ReactNode =>
  rows.length === 0 ? (
    <p className="text-(length:--text-sm) text-(--text-muted)">Nada pago.</p>
  ) : (
    <ul className="flex flex-col gap-1 text-(length:--text-sm)">
      {rows.map((row) => (
        <li key={row.key} className="flex justify-between gap-2">
          {row.onClick === undefined ? (
            <span className="truncate">{row.label}</span>
          ) : (
            <button
              type="button"
              className="truncate text-left underline-offset-2 hover:underline"
              onClick={row.onClick}
            >
              {row.label}
            </button>
          )}
          <span className="shrink-0 font-(--font-mono) tabular-nums">
            {money(row.amount)}{' '}
            <span className="text-(--text-muted)">({String(row.count)})</span>
          </span>
        </li>
      ))}
    </ul>
  )
